/**
 * QR-Join-App (Doc 13 §3.1):
 *  1. /join/<token> → GET /api/join/:token → Titel, Sprache, bekannte Sprecher
 *  2. Quellen anlegen: Gerät (nach Mic-Freigabe mit Labels), Kanal, Art
 *     (Ansteckmikro/Raum/Remote/Konferenz), Sprechername, Modus (Listener/Push-to-Talk)
 *  3. Start/Stop pro Quelle; Status: Pegel, Modus, „Gateway hört", letzte
 *     eigene Transkriptzeile (Transcript-Socket, joinToken, scope own)
 *  4. PTT: großer Button mit Pointer-Capture (pointerdown/up/cancel)
 *  5. Konferenz (Doc 15 §1.2): Tab-/Fenster-Audio per getDisplayMedia statt
 *     Mikrofon; der Stream lebt nur im Speicher dieser Seite (nach Reload neu wählen)
 *  6. Embed-Modus (Regie-Browser, 12.10.): `?embed=1` kompakt, `preset=` öffnet
 *     ohne gespeicherte Quellen direkt das Formular (`name=` als Vorgabe);
 *     postMessage `{ type: 'voice-join', event, sourceId?, kind?, message? }` an
 *     `window.parent`, nur an die Origin aus `document.referrer` (ohne Referrer
 *     kein Post). Siehe `parseEmbedOptions`/`embedMessage` in store.ts.
 * Konfiguration bleibt im localStorage (stabile source.id).
 */
import './style.css';
import { newSourceId, TranscriptClient, VoiceCapture } from '@voice/capture';
import type { JoinInfo, TranscriptServerMessage } from '@voice/protocol';
import {
  channelLabel,
  DISPLAY_TEXT,
  displayAudioSupported,
  displayPickLabel,
  embedMessage,
  initialDraft,
  inputLabel,
  kindBadge,
  kindLabel,
  loadState,
  modeHint,
  newStoredSource,
  parentOrigin,
  parseEmbedOptions,
  presetOf,
  saveState,
  toSourceConfig,
  tokenFromPath,
  vadOverrides,
  withPreset,
  wsBase,
  type EmbedEvent,
  type Preset,
  type StoredSource,
  type StoredState,
} from './store.js';

// ------------------------------------------------------------------ Zustand

interface Runtime {
  capture: VoiceCapture | null;
  state: string;
  error: string | null;
  level: number;
  vad: boolean;
  speaking: boolean;
  lastFinal: string;
  partial: string;
  regieLabel: string | null;
  revoked: boolean;
  /** Hinweis bei gestoppter Quelle (z. B. Teilen beendet), bis zum nächsten Start */
  notice: string | null;
  /** Embed: zuletzt an die Regie gemeldetes Ereignis (keine Doppelmeldungen) */
  posted: EmbedEvent | null;
  els: CardEls | null;
}

interface CardEls {
  status: HTMLElement;
  meter: HTMLElement;
  line: HTMLElement;
  input: HTMLElement;
  toggle: HTMLButtonElement;
  ptt: HTMLButtonElement | null;
  /** nur Konferenz: Tab (erneut) wählen */
  pick: HTMLButtonElement | null;
}

const storage = (() => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
})();

const stored: StoredState = loadState(storage);
let token: string | null = tokenFromPath(location.pathname);
let join: JoinInfo | null = null;
let joinError: string | null = null;
let sessionEnded = false;
let transcriptState = 'aus';
let transcript: TranscriptClient | null = null;
let editing: StoredSource | null = null;
let devices: MediaDeviceInfo[] = [];
let micGranted = false;
let wakeLock: { release(): Promise<void> } | null = null;
const rt = new Map<string, Runtime>();
/** Konferenz: geteilte Tab-/Fenster-Streams pro source.id (nicht persistierbar) */
const displayStreams = new Map<string, MediaStream>();
/** Fehler beim Tab-Wählen im Formular (z. B. ohne „Audio teilen") */
let pickError: string | null = null;

const app = document.getElementById('app') as HTMLElement;
const embedOpts = parseEmbedOptions(location.search);
const embed = embedOpts.embed;
if (embed) document.documentElement.classList.add('embed');
/** Embed: Ziel-Origin der Regie (aus document.referrer); null = nicht posten */
const parentTarget = embed && window.parent !== window ? parentOrigin(document.referrer) : null;
const secure = window.isSecureContext && Boolean(navigator.mediaDevices?.getUserMedia);
const displaySupported = window.isSecureContext && displayAudioSupported({ getDisplayMedia: navigator.mediaDevices?.getDisplayMedia, userAgent: navigator.userAgent });

function runtime(id: string): Runtime {
  let r = rt.get(id);
  if (!r) {
    r = { capture: null, state: 'idle', error: null, level: 0, vad: false, speaking: false, lastFinal: '', partial: '', regieLabel: null, revoked: false, notice: null, posted: null, els: null };
    rt.set(id, r);
  }
  return r;
}

function persist(): void {
  saveState(storage, stored);
}

/** Embed: Ereignis an die einbettende Regie (nur bekannte Origin, nie `'*'`). */
function notifyParent(event: EmbedEvent, extra: { sourceId?: string; kind?: Preset; message?: string | null } = {}): void {
  if (!parentTarget) return;
  try {
    window.parent.postMessage(embedMessage(event, extra), parentTarget);
  } catch {
    /* Regie weg/ungültige Origin — Embed-Meldungen sind optional */
  }
}

/** Quellen-Ereignis melden; `stopped` nur nach `started`, keine Wiederholung desselben Ereignisses. */
function notifySource(s: StoredSource, event: Exclude<EmbedEvent, 'ready'>, message?: string | null): void {
  const r = runtime(s.id);
  if (r.posted === event || (event === 'stopped' && r.posted !== 'started')) return;
  r.posted = event;
  notifyParent(event, { sourceId: s.id, kind: presetOf(s), message: message ?? null });
}

// ------------------------------------------------------------------ DOM-Helfer

type Child = Node | string | null | undefined | false;
function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, unknown> = {}, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === false) continue;
    if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2), v as EventListener);
    else if (k === 'class') el.className = String(v);
    else if (k in el && k !== 'list') (el as unknown as Record<string, unknown>)[k] = v;
    else el.setAttribute(k, v === true ? '' : String(v));
  }
  for (const c of children) if (c !== null && c !== undefined && c !== false) el.append(c);
  return el;
}

// ------------------------------------------------------------------ Ablauf

async function init(): Promise<void> {
  if (!token && stored.lastToken) {
    token = stored.lastToken;
    history.replaceState(null, '', `/join/${token}${location.search}`);
  }
  if (token) await loadJoin();
  // preset=… : ohne gespeicherte Quellen direkt das Formular (nur beim ersten Laden)
  if (join && !editing) editing = initialDraft(embedOpts, stored.sources.length, newSourceId());
  render();
  if (join) notifyParent('ready');
  else notifyParent('error', { message: joinError ?? 'keine Einladung' });
}

async function loadJoin(): Promise<void> {
  if (!token) return;
  try {
    const res = await fetch(`/api/join/${encodeURIComponent(token)}`, { cache: 'no-store' });
    if (res.status === 410) {
      joinError = 'Diese Einladung ist abgelaufen oder die Session wurde beendet. Bitte den aktuellen QR-Code der Regie scannen.';
      return;
    }
    if (!res.ok) {
      joinError = 'Einladung unbekannt. Bitte den QR-Code der Regie erneut scannen.';
      return;
    }
    join = (await res.json()) as JoinInfo;
    joinError = null;
    stored.lastToken = token;
    persist();
    connectTranscript();
  } catch {
    joinError = 'Gateway nicht erreichbar. Netzwerk prüfen und neu laden.';
  }
}

function connectTranscript(): void {
  if (!join || !token) return;
  const ids = stored.sources.map((s) => s.id);
  if (transcript) {
    transcript.setSourceIds(ids);
    return;
  }
  transcript = new TranscriptClient({
    gatewayUrl: wsBase(location.origin),
    sessionId: join.sessionId,
    joinToken: token,
    scope: 'own',
    sourceIds: ids,
    onMessage: onTranscript,
    onState: (s) => {
      transcriptState = s === 'open' ? 'verbunden' : s === 'reconnecting' ? 'verbinde neu…' : s === 'error' ? 'Fehler' : s;
      const el = document.getElementById('transcript-state');
      if (el) el.textContent = embed && join ? `${join.title || 'Workshop'} · Transkript: ${transcriptState}` : `Transkript: ${transcriptState}`;
    },
  });
  transcript.connect();
}

function onTranscript(msg: TranscriptServerMessage): void {
  switch (msg.type) {
    case 'transcript': {
      const r = runtime(msg.speaker.sourceId);
      if (msg.status === 'final') {
        if (msg.text) r.lastFinal = msg.text;
        r.partial = '';
      } else {
        r.partial = msg.text;
      }
      updateCard(msg.speaker.sourceId);
      break;
    }
    case 'speaker_updated': {
      const s = stored.sources.find((x) => x.id === msg.speaker.sourceId);
      const r = runtime(msg.speaker.sourceId);
      r.regieLabel = s && msg.speaker.label && msg.speaker.label !== s.label ? msg.speaker.label : null;
      updateCard(msg.speaker.sourceId);
      break;
    }
    case 'source':
      if (msg.source.state === 'left' && msg.source.reason === 'revoked') {
        const r = runtime(msg.source.id);
        r.revoked = true;
        r.error = 'Von der Regie getrennt.';
        updateCard(msg.source.id);
      }
      break;
    case 'status':
      if (msg.state === 'ended') {
        sessionEnded = true;
        void stopAll().then(() => {
          // Teilen beenden, damit die Browser-Leiste „… wird geteilt" verschwindet
          for (const id of [...displayStreams.keys()]) releaseDisplay(id);
          render();
        });
      }
      break;
    default:
      break;
  }
}

async function startSource(s: StoredSource): Promise<void> {
  if (!join || !token) return;
  const r = runtime(s.id);
  if (r.capture) return;
  r.error = null;
  r.notice = null;
  r.revoked = false;
  r.posted = null;
  const display = s.input === 'display' ? liveDisplay(s.id) : null;
  if (s.input === 'display' && !display) {
    r.notice = DISPLAY_TEXT.notPicked;
    updateCard(s.id);
    return;
  }
  const cap = new VoiceCapture({
    gatewayUrl: `${wsBase(location.origin)}/capture`,
    joinToken: token,
    source: toSourceConfig(s, join.language),
    vad: vadOverrides(s),
    ...(display ? { stream: display } : s.deviceId ? { deviceId: s.deviceId } : {}),
  });
  r.capture = cap;
  // Nutzer beendet das Teilen in der Browser-Leiste → Capture stoppt selbst (statechange stopped folgt)
  cap.addEventListener('ended', () => {
    r.notice = DISPLAY_TEXT.ended;
    releaseDisplay(s.id);
    updateCard(s.id);
  });
  cap.addEventListener('statechange', (e) => {
    const d = (e as CustomEvent<{ state: string; error?: string }>).detail;
    r.state = d.state;
    if (d.error) r.error = d.error;
    if (d.state === 'error' || d.state === 'stopped') {
      r.capture = null;
      r.speaking = false;
    }
    if (d.state === 'streaming') notifySource(s, 'started');
    else if (d.state === 'stopped') notifySource(s, 'stopped');
    // verzögert: start()-catch bzw. das 'error'-Event setzen erst danach den lesbaren Text
    else if (d.state === 'error') setTimeout(() => notifySource(s, 'error', r.error ?? d.error), 0);
    updateCard(s.id);
    void updateWakeLock();
  });
  cap.addEventListener('level', (e) => {
    const d = (e as CustomEvent<{ rms: number; vad: boolean }>).detail;
    r.level = d.rms;
    r.vad = d.vad;
    updateLive(s.id);
  });
  cap.addEventListener('speech', (e) => {
    r.speaking = (e as CustomEvent<{ state: string }>).detail.state === 'start';
    updateLive(s.id);
  });
  cap.addEventListener('error', (e) => {
    const d = (e as CustomEvent<{ code: string; message: string }>).detail;
    r.error = errorText(d.code, d.message);
    updateCard(s.id);
  });
  updateCard(s.id);
  try {
    await cap.start();
  } catch (err) {
    r.capture = null;
    const e = err as { code?: string; message?: string; name?: string };
    r.error = e.name === 'NotAllowedError' ? 'Mikrofon-Zugriff verweigert. In den Browser-Einstellungen erlauben.' : errorText(e.code ?? '', e.message ?? String(err));
    r.state = 'error';
    notifySource(s, 'error', r.error);
    updateCard(s.id);
  }
}

async function stopSource(id: string): Promise<void> {
  const r = runtime(id);
  const cap = r.capture;
  r.capture = null;
  r.speaking = false;
  if (cap) await cap.stop().catch(() => undefined);
  r.state = 'stopped';
  const s = stored.sources.find((x) => x.id === id);
  if (s) notifySource(s, 'stopped');
  updateCard(id);
  void updateWakeLock();
}

async function stopAll(): Promise<void> {
  await Promise.all(stored.sources.map((s) => stopSource(s.id)));
}

function errorText(code: string, message: string): string {
  switch (code) {
    case 'engine_unavailable':
      return message || 'Für diese Quellenart ist im Gateway noch keine Spracherkennung verfügbar.';
    case 'auth_failed':
      return 'Einladung ungültig oder abgelaufen. Bitte QR-Code erneut scannen.';
    case 'revoked':
      return message || 'Von der Regie getrennt.';
    case 'busy':
      return 'Gateway ausgelastet (zu viele Quellen). Später erneut versuchen.';
    case 'no_audio_track':
      return DISPLAY_TEXT.noAudio;
    case 'track_ended':
      return DISPLAY_TEXT.ended;
    default:
      return message || code || 'Unbekannter Fehler';
  }
}

async function updateWakeLock(): Promise<void> {
  const any = [...rt.values()].some((r) => r.capture && r.state === 'streaming');
  const nav = navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> } };
  try {
    if (any && !wakeLock && nav.wakeLock) wakeLock = await nav.wakeLock.request('screen');
    if (!any && wakeLock) {
      await wakeLock.release();
      wakeLock = null;
    }
  } catch {
    /* Wake Lock optional */
  }
}

// ------------------------------------------------------------------ Geräte

async function grantMic(): Promise<void> {
  try {
    const st = await navigator.mediaDevices.getUserMedia({ audio: true });
    st.getTracks().forEach((t) => t.stop());
    micGranted = true;
    devices = await VoiceCapture.listMicrophones();
  } catch (err) {
    alert(`Mikrofon-Freigabe fehlgeschlagen: ${(err as Error).message}`);
  }
  render();
}

// ------------------------------------------------------------------ Tab-Audio (Konferenz)

/** Gehaltener Display-Stream der Quelle, solange seine Audio-Spur lebt. */
function liveDisplay(id: string): MediaStream | null {
  const st = displayStreams.get(id);
  return st && st.getAudioTracks().some((t) => t.readyState === 'live') ? st : null;
}

/** Teilen beenden und vergessen (Quelle entfernt, Preset gewechselt, Session vorbei). */
function releaseDisplay(id: string): void {
  displayStreams.get(id)?.getTracks().forEach((t) => t.stop());
  displayStreams.delete(id);
}

/**
 * Tab oder Fenster wählen: getDisplayMedia mit Video (Chrome verlangt es),
 * Video-Spur sofort stoppen, Audio-Spur für diese Quelle halten. Ohne
 * Audio-Spur (nicht „Audio teilen" angehakt) → Fehlertext, nichts gehalten.
 */
async function pickDisplay(id: string): Promise<string | null> {
  let st: MediaStream;
  try {
    st = await navigator.mediaDevices.getDisplayMedia({ audio: true, video: true });
  } catch (err) {
    const e = err as { name?: string; message?: string };
    // Abbrechen im Auswahldialog ist kein Fehler
    return e.name === 'NotAllowedError' || e.name === 'AbortError' ? null : `Teilen fehlgeschlagen: ${e.message ?? String(err)}`;
  }
  st.getVideoTracks().forEach((t) => t.stop());
  if (st.getAudioTracks().length === 0) {
    st.getTracks().forEach((t) => t.stop());
    return DISPLAY_TEXT.noAudio;
  }
  if (displayStreams.get(id) !== st) releaseDisplay(id);
  displayStreams.set(id, st);
  for (const t of st.getAudioTracks()) {
    t.addEventListener('ended', () => {
      if (displayStreams.get(id) !== st) return;
      releaseDisplay(id);
      const r = runtime(id);
      if (!r.capture) r.notice = DISPLAY_TEXT.ended;
      updateCard(id);
    });
  }
  return null;
}

/** Kanalzahl eines Geräts ermitteln (Browser liefern zuverlässig höchstens 2). */
async function probeChannels(deviceId: string | null): Promise<number> {
  try {
    const st = await navigator.mediaDevices.getUserMedia({
      audio: { ...(deviceId ? { deviceId: { exact: deviceId } } : {}), channelCount: { ideal: 2 }, echoCancellation: false, noiseSuppression: false, autoGainControl: false },
    });
    const n = st.getAudioTracks()[0]?.getSettings().channelCount ?? 1;
    st.getTracks().forEach((t) => t.stop());
    return n;
  } catch {
    return 1;
  }
}

// ------------------------------------------------------------------ Render

function render(): void {
  app.replaceChildren(renderHeader(), ...renderBody());
  for (const s of stored.sources) updateCard(s.id);
}

function renderHeader(): HTMLElement {
  if (embed) {
    // kompakt: eine Zeile statt Marke/Titel
    return h(
      'header',
      { class: 'top compact' },
      h('p', { class: 'muted small', id: 'transcript-state' }, join ? `${join.title || 'Workshop'} · Transkript: ${transcriptState}` : 'Mikrofon verbinden'),
    );
  }
  return h(
    'header',
    { class: 'top' },
    h('div', { class: 'brand' }, h('img', { src: '/icon.svg', alt: '', width: 28, height: 28 }), h('span', {}, 'Bentimento Mikrofon')),
    join ? h('h1', {}, join.title || 'Workshop') : h('h1', {}, 'Mikrofon verbinden'),
    join ? h('p', { class: 'muted', id: 'transcript-state' }, `Sprache: ${join.language} · Transkript: ${transcriptState}`) : null,
  );
}

function renderBody(): HTMLElement[] {
  const out: HTMLElement[] = [];
  if (!secure) {
    out.push(h('div', { class: 'banner error' }, 'Das Mikrofon ist nur über HTTPS (oder localhost) verfügbar. Bitte die Einladung über die https-Adresse öffnen.'));
  }
  if (sessionEnded) out.push(h('div', { class: 'banner' }, 'Die Session wurde von der Regie beendet. Danke!'));
  if (!token || joinError) {
    out.push(renderTokenEntry());
    return out;
  }
  if (!join) {
    out.push(h('p', { class: 'muted' }, 'Lade Einladung…'));
    return out;
  }
  const list = h('section', { class: 'sources' });
  if (stored.sources.length === 0 && !editing && !embed) {
    list.append(h('p', { class: 'muted' }, 'Noch keine Quelle. Lege dein Mikrofon an — meist ein Ansteckmikro mit deinem Namen.'));
  }
  for (const s of stored.sources) list.append(renderCard(s));
  out.push(list);
  if (editing) out.push(renderForm(editing));
  else if (!sessionEnded) {
    out.push(
      h(
        'button',
        {
          class: 'btn primary wide',
          onclick: () => {
            editing = newStoredSource(newSourceId(), 'lapel', '');
            render();
          },
        },
        '+ Quelle hinzufügen',
      ),
    );
  }
  return out;
}

function renderTokenEntry(): HTMLElement {
  const input = h('input', { type: 'text', placeholder: 'XXXXXXXX-XXXXXXXXXXXXXXXX', autocapitalize: 'characters', spellcheck: false, value: token ?? '' });
  return h(
    'section',
    { class: 'card' },
    h('p', {}, joinError ?? 'Bitte den QR-Code der Regie scannen — oder den Einladungscode hier eingeben.'),
    h(
      'form',
      {
        class: 'row',
        onsubmit: (e: Event) => {
          e.preventDefault();
          const t = tokenFromPath(`/join/${input.value.trim()}`);
          if (!t) return alert('Ungültiger Code');
          location.assign(`/join/${t}`);
        },
      },
      input,
      h('button', { class: 'btn primary', type: 'submit' }, 'Beitreten'),
    ),
  );
}

function renderCard(s: StoredSource): HTMLElement {
  const r = runtime(s.id);
  const status = h('div', { class: 'status' });
  const meter = h('div', { class: 'meter-fill' });
  const line = h('div', { class: 'line' });
  const input = h('div', { class: 'muted small' });
  const toggle = h('button', { class: 'btn', onclick: () => void (r.capture ? stopSource(s.id) : startSource(s)) });
  let ptt: HTMLButtonElement | null = null;
  if (s.mode === 'ptt') {
    ptt = h('button', { class: 'ptt', type: 'button', 'aria-label': 'Gedrückt halten zum Sprechen' }, 'Gedrückt halten zum Sprechen');
    attachPtt(ptt, s.id);
  }
  let pick: HTMLButtonElement | null = null;
  if (s.input === 'display') {
    pick = h('button', {
      class: 'btn',
      type: 'button',
      onclick: async () => {
        const err = await pickDisplay(s.id);
        r.notice = err;
        updateCard(s.id);
      },
    });
  }
  r.els = { status, meter, line, input, toggle, ptt, pick };
  const preset = presetOf(s);
  return h(
    'article',
    { class: 'card source' },
    h(
      'div',
      { class: 'card-head' },
      h('div', {}, h('div', { class: 'name' }, s.label || '(ohne Namen)'), h('div', { class: 'muted small' }, `${kindLabel(preset)} · ${s.mode === 'ptt' ? 'Push-to-Talk' : 'Dauer-Listener'}`)),
      h('span', { class: `badge ${preset}` }, kindBadge(preset)),
    ),
    input,
    h('div', { class: 'meter', role: 'meter', 'aria-label': 'Pegel' }, meter),
    status,
    line,
    ptt,
    h(
      'div',
      { class: 'row actions wrap' },
      pick,
      toggle,
      h(
        'button',
        {
          class: 'btn ghost',
          onclick: async () => {
            await stopSource(s.id);
            editing = { ...s };
            render();
          },
        },
        'Bearbeiten',
      ),
      h(
        'button',
        {
          class: 'btn ghost danger',
          onclick: async () => {
            if (!confirm(`Quelle „${s.label}" entfernen?`)) return;
            await stopSource(s.id);
            releaseDisplay(s.id);
            stored.sources = stored.sources.filter((x) => x.id !== s.id);
            persist();
            connectTranscript();
            render();
          },
        },
        'Entfernen',
      ),
    ),
  );
}

/** Pointer-Capture wie im CogitoChatInput: kein mouseleave, Loslassen auch außerhalb des Buttons. */
function attachPtt(btn: HTMLButtonElement, id: string): void {
  let pressed = false;
  const down = (e: PointerEvent) => {
    const cap = runtime(id).capture;
    if (!cap || runtime(id).state !== 'streaming') return;
    e.preventDefault();
    btn.setPointerCapture(e.pointerId);
    pressed = true;
    btn.classList.add('down');
    cap.pttDown();
  };
  const up = () => {
    if (!pressed) return;
    pressed = false;
    btn.classList.remove('down');
    runtime(id).capture?.pttUp();
  };
  btn.addEventListener('pointerdown', down);
  btn.addEventListener('pointerup', up);
  btn.addEventListener('pointercancel', up);
  btn.addEventListener('lostpointercapture', up);
  btn.addEventListener('contextmenu', (e) => e.preventDefault());
  // Tastatur (Leertaste/Enter gedrückt halten)
  btn.addEventListener('keydown', (e) => {
    if ((e.key === ' ' || e.key === 'Enter') && !e.repeat && !pressed && runtime(id).capture) {
      e.preventDefault();
      pressed = true;
      btn.classList.add('down');
      runtime(id).capture?.pttDown();
    }
  });
  btn.addEventListener('keyup', (e) => {
    if (e.key === ' ' || e.key === 'Enter') up();
  });
  btn.addEventListener('blur', up);
}

function statusText(r: Runtime, s: StoredSource): string {
  if (r.error && (r.state === 'error' || r.revoked)) return r.error;
  switch (r.state) {
    case 'requesting-mic':
      return 'Mikrofon wird freigegeben…';
    case 'connecting':
      return 'Verbinde mit dem Gateway…';
    case 'reconnecting':
      return 'Verbindung unterbrochen – verbinde neu…';
    case 'streaming': {
      const talking = r.speaking ? (s.mode === 'ptt' ? ' · sendet' : ' · Sprache erkannt') : s.mode === 'ptt' ? ' · Taste halten zum Sprechen' : ' · wartet auf Sprache';
      return `● Gateway hört${talking}`;
    }
    default:
      return r.notice ?? (s.input === 'display' && !liveDisplay(s.id) ? DISPLAY_TEXT.notPicked : 'Aus');
  }
}

function updateLive(id: string): void {
  const r = rt.get(id);
  const s = stored.sources.find((x) => x.id === id);
  if (!r?.els || !s) return;
  r.els.meter.style.width = `${Math.min(100, Math.round(Math.sqrt(r.level) * 160))}%`;
  r.els.meter.classList.toggle('active', r.vad || r.speaking);
  r.els.status.textContent = statusText(r, s);
  r.els.status.className = `status ${r.state === 'streaming' ? 'on' : ''} ${r.state === 'error' || r.revoked ? 'err' : ''}`;
}

function updateCard(id: string): void {
  const r = rt.get(id);
  const s = stored.sources.find((x) => x.id === id);
  if (!r?.els || !s) return;
  updateLive(id);
  const els = r.els;
  els.line.replaceChildren(
    r.regieLabel ? h('div', { class: 'muted small' }, `In der Regie als „${r.regieLabel}"`) : '',
    r.lastFinal ? h('div', { class: 'final' }, `„${r.lastFinal}"`) : '',
    r.partial ? h('div', { class: 'partial' }, r.partial) : '',
    !r.lastFinal && !r.partial && r.state === 'streaming' && !embed ? h('div', { class: 'muted small' }, 'Deine letzte erkannte Äußerung erscheint hier.') : '',
  );
  const running = Boolean(r.capture);
  const display = s.input === 'display';
  const hasStream = display && liveDisplay(id) !== null;
  els.input.textContent = inputLabel(s, hasStream);
  els.toggle.textContent = running ? 'Stopp' : 'Start';
  els.toggle.className = running ? 'btn' : 'btn primary';
  els.toggle.disabled = sessionEnded || r.revoked || !secure || (display && !hasStream && !running);
  if (els.ptt) els.ptt.disabled = r.state !== 'streaming';
  if (els.pick) {
    // Während der Aufnahme kein Tabwechsel: stop() der alten Spur meldet kein `ended`.
    els.pick.hidden = running || sessionEnded || !displaySupported;
    els.pick.textContent = displayPickLabel(hasStream, true);
    els.pick.className = hasStream ? 'btn ghost' : 'btn primary';
  }
}

function renderForm(s: StoredSource): HTMLElement {
  const known = join?.knownSpeakers ?? [];
  const preset = presetOf(s);
  const conference = preset === 'conference';
  const saved = stored.sources.some((x) => x.id === s.id);
  const nameInput = h('input', {
    type: 'text',
    value: s.label,
    required: true,
    maxLength: 80,
    placeholder: conference ? 'z. B. Konferenz Teams' : s.kind === 'room' ? 'z. B. Raum Cisco Board' : 'Dein Name',
    autocomplete: 'name',
  });
  nameInput.setAttribute('list', 'known-speakers');
  const datalist = h('datalist', { id: 'known-speakers' }, ...known.map((k) => h('option', { value: k.label })));
  const deviceSel = h('select', {}, h('option', { value: '' }, 'Standard-Mikrofon'), ...devices.map((d) => h('option', { value: d.deviceId, selected: d.deviceId === s.deviceId }, d.label || 'Mikrofon')));
  const chanSel = h('select', {});
  const fillChannels = (n: number) => {
    const opts: Array<[string, string]> = [['', channelLabel(null, n)]];
    for (let c = 0; c < n; c++) opts.push([String(c), channelLabel(c, n)]);
    chanSel.replaceChildren(...opts.map(([v, l]) => h('option', { value: v, selected: v === (s.channel === null ? '' : String(s.channel)) }, l)));
  };
  fillChannels(s.channels ?? 1);
  deviceSel.addEventListener('change', async () => {
    s.deviceId = deviceSel.value || null;
    s.deviceLabel = deviceSel.selectedOptions[0]?.textContent ?? null;
    if (s.deviceLabel === 'Standard-Mikrofon') s.deviceLabel = null;
    s.channels = await probeChannels(s.deviceId);
    s.channel = null;
    fillChannels(s.channels);
  });
  if (!conference && micGranted && s.channels === null) void probeChannels(s.deviceId).then((n) => ((s.channels = n), fillChannels(n)));

  const radio = (name: string, value: string, checked: boolean, label: string, onchange: () => void, disabled = false, note: string | null = null) =>
    h(
      'label',
      { class: disabled ? 'choice disabled' : 'choice' },
      h('input', { type: 'radio', name, value, checked, onchange, disabled }),
      h('span', {}, label, note ? h('span', { class: 'hint small' }, h('br'), note) : null),
    );
  const hintEl = h('p', { class: 'hint small' });
  const updateHint = () => {
    const text = modeHint(s);
    hintEl.textContent = text ?? '';
    hintEl.hidden = !text;
    hintEl.classList.toggle('warn', s.kind === 'remote' && s.mode === 'listener');
  };
  updateHint();
  // bereits getippten Namen über das Neu-Rendern retten
  const keepName = () => (s.label = nameInput.value.trim());
  const setPreset = (p: Preset) => {
    keepName();
    pickError = null;
    Object.assign(s, withPreset(s, p));
    render();
  };
  const procBox = (key: 'ec' | 'ns' | 'agc', label: string) =>
    h('label', { class: 'check' }, h('input', { type: 'checkbox', checked: s.processing[key], onchange: (e: Event) => (s.processing[key] = (e.target as HTMLInputElement).checked) }), label);

  // Konferenz: Tab/Fenster statt Gerät und Kanal
  const hasStream = conference && liveDisplay(s.id) !== null;
  const displayBlock = conference
    ? h(
        'div',
        { class: 'field' },
        h('span', {}, 'Konferenz-Audio'),
        h(
          'div',
          { class: 'row wrap' },
          h(
            'button',
            {
              type: 'button',
              class: hasStream ? 'btn' : 'btn primary',
              disabled: !displaySupported,
              onclick: async () => {
                keepName();
                pickError = await pickDisplay(s.id);
                render();
              },
            },
            displayPickLabel(hasStream, saved),
          ),
          h('span', { class: 'muted small' }, hasStream ? 'Tab-Audio geteilt' : 'noch kein Tab gewählt'),
        ),
        pickError ? h('p', { class: 'hint small err' }, pickError) : null,
        embed ? null : h('p', { class: 'hint small' }, DISPLAY_TEXT.shareAudio),
        h('p', { class: 'hint small warn' }, DISPLAY_TEXT.onePath),
      )
    : null;

  return h(
    'form',
    {
      class: 'card form',
      onsubmit: (e: Event) => {
        e.preventDefault();
        s.label = nameInput.value.trim();
        if (!s.label) return;
        s.channel = s.input === 'display' || chanSel.value === '' ? null : Number(chanSel.value);
        const known = known_(s.label);
        s.participationId = known?.participationId ?? s.participationId ?? null;
        // Preset weg von Konferenz → gehaltenes Teilen beenden
        if (s.input !== 'display') releaseDisplay(s.id);
        const i = stored.sources.findIndex((x) => x.id === s.id);
        if (i >= 0) stored.sources[i] = s;
        else stored.sources.push(s);
        persist();
        editing = null;
        pickError = null;
        connectTranscript();
        render();
      },
    },
    h('h2', {}, saved ? 'Quelle bearbeiten' : 'Neue Quelle'),
    !conference && !micGranted
      ? h('div', { class: 'row' }, h('button', { type: 'button', class: 'btn', onclick: () => void grantMic(), disabled: !secure }, 'Mikrofon freigeben'), embed ? null : h('span', { class: 'muted small' }, 'zeigt Gerätenamen und Kanäle'))
      : null,
    conference ? null : h('label', { class: 'field' }, h('span', {}, 'Gerät'), deviceSel),
    conference ? null : h('label', { class: 'field' }, h('span', {}, 'Kanal'), chanSel),
    h(
      'fieldset',
      {},
      h('legend', {}, 'Art'),
      radio('kind', 'lapel', preset === 'lapel', 'Ansteckmikro (ein Sprecher)', () => setPreset('lapel')),
      radio('kind', 'room', preset === 'room', 'Raum (mehrere Sprecher)', () => setPreset('room')),
      radio('kind', 'remote', preset === 'remote', 'Remote (Videokonferenz, eigener Laptop)', () => setPreset('remote')),
      radio('kind', 'conference', conference, 'Konferenz (Teams/Zoom im Browser-Tab)', () => setPreset('conference'), !displaySupported && !conference, displaySupported ? null : DISPLAY_TEXT.unsupported),
    ),
    displayBlock,
    h('label', { class: 'field' }, h('span', {}, s.kind === 'room' ? 'Name der Quelle' : 'Sprecher'), nameInput, datalist),
    conference
      ? null
      : h(
          'fieldset',
          {},
          h('legend', {}, 'Modus'),
          radio('mode', 'listener', s.mode === 'listener', 'Dauer-Listener (Sprache wird automatisch erkannt)', () => ((s.mode = 'listener'), updateHint())),
          radio('mode', 'ptt', s.mode === 'ptt', 'Push-to-Talk (Taste gedrückt halten)', () => ((s.mode = 'ptt'), updateHint())),
          hintEl,
        ),
    conference
      ? null
      : h('details', {}, h('summary', {}, 'Audio-Verarbeitung'), h('div', { class: 'row wrap' }, procBox('ec', 'Echo-Unterdrückung'), procBox('ns', 'Rauschunterdrückung'), procBox('agc', 'Auto-Pegel'))),
    h(
      'div',
      { class: 'row actions' },
      h('button', { type: 'submit', class: 'btn primary' }, 'Speichern'),
      h(
        'button',
        {
          type: 'button',
          class: 'btn ghost',
          onclick: () => {
            // neue Quelle verworfen → ihr Teilen beenden; bestehende behält ihren Stream
            const keep = stored.sources.find((x) => x.id === s.id);
            if (!keep || keep.input !== 'display') releaseDisplay(s.id);
            editing = null;
            pickError = null;
            render();
          },
        },
        'Abbrechen',
      ),
    ),
  );

  function known_(label: string) {
    return known.find((k) => k.label.toLowerCase() === label.toLowerCase());
  }
}

window.addEventListener('pagehide', () => {
  transcript?.close();
});

void init();
